<template>
  <div v-loading="!isLoaded">
    <table
      v-if="isLoaded"
      v-test:table="'rate attributes'"
    >
      <thead>
        <template v-if="isStacked">
          <tr>
            <th>{{ tierGroup.name }}</th>
            <th v-if="isStackedIncludingAgeBanded">
              <template v-if="stackedAttributeTypes.includes(compositeType)">
                {{ compositeType | tierGroupName }} /
              </template>
              {{ ageBandedType | tierGroupName }}
            </th>
            <th>In-Force Rate{{ isStackedIncludingAgeBanded ? 's' : '' }}</th>
          </tr>
        </template>
        <template v-else>
          <!-- Titles for Tier Groups that have multiple `subtypes` -->
          <tr v-if="tierSubtypes.length > 1">
            <th
              v-for="({ name }) in tierSubtypes"
              :key="name"
              colspan="2"
              class="subtype-header"
              v-text="name"
            />
          </tr>
          <!-- Titles for `subtypes`-->
          <tr>
            <template v-for="({ attributeType, name }) in tierSubtypes">
              <th
                v-if="attributeType === ageBandedType"
                :key="`${attributeType}-${name}`"
                v-text="$options.filters.tierGroupName(attributeType)"
              />
              <th
                :key="name"
                :colspan="!isComposite && attributeType === ageBandedType
                  ? 1
                  : 2
                "
                v-text="`In-Force Rate${ attributeType === ageBandedType ? 's' : ''}`"
              />
            </template>
          </tr>
        </template>
      </thead>
      <tbody v-if="tableRows.length">
        <template v-if="isStacked">
          <template v-for="({ name, rateValue }) in tableRows">
            <tr
              v-for="(value, idx) in rateValue.values"
              :key="`${name}-${value.display_label}`"
            >
              <!-- First td in stacked only shows on non-composite and only first the first row.  -->
              <td
                v-if="!idx && name !== compositeType"
                class="subtype-header"
                :rowspan="rateValue.values.length"
                v-text="name"
              />
              <td
                v-if="isStackedIncludingAgeBanded && value.label !== 'composite'"
                v-text="$options.filters.ageBanded(value.display_label)"
              />
              <td-value
                v-bind="{
                  cellData: value,
                  colspan: isStackedIncludingAgeBanded && value.label === 'composite'
                    ? 2
                    : 1,
                }"
              />
            </tr>
          </template>
        </template>
        <template v-else>
          <tr
            v-for="(subtypeValues, idx) in tableRows"
            :key="idx"
          >
            <!-- Each subtypeValues is grouped in the table as 2 columns in the table -->
            <template v-for="(subtypeValue, subtypeValueIdx) in subtypeValues">
              <td
                v-if="!isComposite && tierSubtypes[subtypeValueIdx].attributeType === ageBandedType"
                :key="`${subtypeValueIdx}-label`"
              >
                <template v-if="tierSubtypes[subtypeValueIdx].attributeType !== compositeType">
                  {{ subtypeValue.display_label | ageBanded }}
                </template>
              </td>
              <td-value
                :key="`${subtypeValueIdx}-value`"
                v-bind="{
                  cellData: subtypeValue,
                  colspan: !isComposite && tierSubtypes[subtypeValueIdx].attributeType === ageBandedType
                    ? 1
                    : 2,
                }"
              />
            </template>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
  // vuex
  import { mapState } from 'vuex';
  // components
  import TdValue from './TdValue';

  /**
   * This component is used to display the Rate Values for a given rate attribute.
   * Since there is no reactivity (for now), the temlate logic is created on load.
   *
   * @vuedoc
   * @exports Rates/Table
   * @category Components
   */
  export default {
    name: 'RatesTable',
    components: { TdValue },
    props: {
      rateAttribute: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      ageBandedType: 'AgeBandedRateValue',
      compositeType: 'CompositeRateValue',
      isLoaded: false,
      stackedAttributeTypes: [],
      tableRows: [],
      tierSubtypes: [],
    }),
    computed: {
      ...mapState('rateAttributes', ['isPlanBased']),
      /**
       * Evaluate the tier subtypes for if there is only a single type with a composite type.
       *
       * @returns {boolean}
       */
      isComposite() {
        return this.tierSubtypes.length === 1
          && this.tierSubtypes.attributeType === this.compositeType;
      },
      /**
       * Evalutate the stacked attribute types for any that are age banded to be used in the template.
       *
       * @returns {boolean}
       */
      isStackedIncludingAgeBanded() {
        return this.stackedAttributeTypes.includes(this.ageBandedType);
      },
      /**
       * Cache the tier group layout to be used throughout the template and createRows logic.
       *
       * @returns {boolean}
       */
      isStacked() {
        return this.tierGroup.layout === 'stacked';
      },
      /**
       * Cache the tier group to be used throughout the template and createRows logic.
       *
       * @returns {object}
       */
      tierGroup() {
        return this.rateAttribute.products[0].tier_group;
      },
    },
    created() {
      this.createRows();
    },
    methods: {
      /**
       * Method to create the rows when the page is first loaded.
       */
      createRows() {
        let id;
        let subtypeValue;
        let type;
        let values;

        if (this.isStacked) {
          this.tierGroup.tier_subtypes.forEach((subtype) => {
            subtypeValue = this.isPlanBased
              ? subtype.rate_value
              : subtype.rate_values[0];
            ({ id } = subtypeValue);

            this.tableRows.push({
              id,
              name: subtype.name,
              rateValue: subtypeValue,
            });

            this.stackedAttributeTypes.push(subtypeValue.type);
          });

          this.isLoaded = true;

          return;
        }

        const rowValues = [];
        let rowLength = 0;

        this.tierGroup.tier_subtypes.forEach((subtype, idx) => {
          subtypeValue = this.isPlanBased
            ? subtype.rate_value
            : subtype.rate_values[0];
          ({ id, type, values } = subtypeValue);

          rowLength = values.length > rowLength
            ? values.length
            : rowLength;

          this.tierSubtypes.push({
            id: subtype.id || this.compositeType,
            name: subtype.name || this.compositeType,
            attributeType: type,
          });

          rowValues.push({ id: (id || idx), values });
        });

        // Loop through max length to create cell groups
        for (let i = 0; i < rowLength; i += 1) {
          const rows = [];

          // Loop through each subtype
          rowValues.forEach((subtype, idx) => {
            rows[idx] = subtype.values[i] || {};
          });
          this.tableRows.push(rows);
        }

        this.isLoaded = true;
      },
    },
  };
</script>

<style lang="scss" scoped>
  table {
    width: 100%;
    table-layout: fixed;
    border: 1px solid $tf-light-gray;

    section:not(.is-active) & {
      border-bottom: none;
    }
  }

  tbody,
  th {
    border: 1px solid $tf-light-gray;
  }

  th {
    text-align: left;
    padding: 10px 20px;
    background: $tf-extra-light-gray-2;

    &.subtype-header {
      text-align: center;
    }
  }

  td {
    border: 1px solid $tf-light-gray;
    padding: 15px 20px;
    vertical-align: top;
  }

  .subtype-header {
    background: $tf-light-blue;
    border: {
      right: 0;
      left: 0;
    }
  }
</style>
